import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button, Form, ListGroup, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import StaffThemeSwitch, { useStaffTheme } from "../components/StaffThemeSwitch";
import { validateLeadTeam } from "../services/leadTrackerApi";
import {
  connectChatEvents,
  getChat,
  getChatMessages,
  linkChatToLead,
  listChats,
  markChatNotCustomer,
  markChatRead,
  searchChatLeadOptions,
  unmarkChatNotCustomer,
} from "../services/whatsappChatApi";
import { openInLeadTracker } from "../utils/leadHandoff";
import "../styles/chats.css";

// The Chats page: the company WhatsApp number's chats, for the Call Team, the Chat Team and admin.
// Step 1 is read only - replies still go from the WhatsApp Business app or WhatsApp Web, and show
// up here as well. Admin sees the same chats without the lead actions.

const PAGE_SIZE = 50;
const MAX_PAGE = 100;
const TOKEN_KEYS = { CALL: "callTeamToken", CHAT: "chatTeamToken", ADMIN: "adminToken" };
const PORTAL_PATHS = { CALL: "/call-team", CHAT: "/chat-team", ADMIN: "/admin" };
const FILTERS = [
  { key: "ALL", label: "All", count: "all" },
  { key: "UNREAD", label: "Unread", count: "unread" },
  { key: "NEEDS_REPLY", label: "Needs reply", count: "needsReply" },
  { key: "MINE", label: "Mine", count: "mine", teamOnly: true },
  { key: "NO_LEAD", label: "No lead", count: "noLead" },
];
const NON_CUSTOMER_CATEGORIES = [["HOTEL", "Hotel"], ["DRIVER", "Driver"], ["VENDOR", "Vendor"], ["OFFICE", "Office"], ["OTHER", "Other"]];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// ---- Small helpers ----

const errorText = (error, fallback) => error?.response?.data?.detail || error?.response?.data?.message || fallback;
const isSignedOut = (error) => error?.response?.status === 401;
const categoryLabel = (category) => (NON_CUSTOMER_CATEGORIES.find(([value]) => value === category) || [category, category || "Other"])[1];

const readToken = (scope) => {
  try {
    return localStorage.getItem(TOKEN_KEYS[scope]) || "";
  } catch {
    return "";
  }
};

const pad = (value) => String(value).padStart(2, "0");
const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const daysAgo = (date, now) => Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
const clock = (date) => `${date.getHours() % 12 || 12}:${pad(date.getMinutes())} ${date.getHours() >= 12 ? "PM" : "AM"}`;
const yearSuffix = (date, now) => (date.getFullYear() !== now.getFullYear() ? ` ${date.getFullYear()}` : "");

// "11:40 AM", "Yesterday", "Sat", "28 Oct" - as the chat list shows the last message.
const listTime = (value, now) => {
  const date = toDate(value);
  if (!date) return "";
  const days = daysAgo(date, now);
  if (days <= 0) return clock(date);
  if (days === 1) return "Yesterday";
  if (days < 7) return WEEKDAYS[date.getDay()];
  return `${date.getDate()} ${MONTHS[date.getMonth()]}${yearSuffix(date, now)}`;
};

const dayLabel = (date, now) => {
  const days = daysAgo(date, now);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days > 1 && days < 7) return WEEKDAYS_LONG[date.getDay()];
  return `${WEEKDAYS_LONG[date.getDay()]}, ${date.getDate()} ${MONTHS_LONG[date.getMonth()]}${yearSuffix(date, now)}`;
};

// "Today 6:00 PM", "Tomorrow 10:00 AM", "Sun 1 Nov, 4:12 PM".
const whenText = (value, now) => {
  const date = toDate(value);
  if (!date) return "";
  const days = daysAgo(date, now);
  if (days === 0) return `Today ${clock(date)}`;
  if (days === -1) return `Tomorrow ${clock(date)}`;
  if (days === 1) return `Yesterday ${clock(date)}`;
  return `${WEEKDAYS[date.getDay()]} ${date.getDate()} ${MONTHS[date.getMonth()]}${yearSuffix(date, now)}, ${clock(date)}`;
};

const fullDate = (value) => {
  const date = toDate(value);
  return date ? `${date.getDate()} ${MONTHS_LONG[date.getMonth()]} ${date.getFullYear()}` : "";
};

const timeLeft = (milliseconds) => {
  const minutes = Math.max(0, Math.floor(milliseconds / 60_000));
  const hours = Math.floor(minutes / 60);
  return hours ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
};

const fileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

// Initials from the WhatsApp name, letters only; a name of emojis or digits gets the person icon.
const initialsOf = (name) => {
  const words = String(name || "").split(/\s+/).filter((word) => /^\p{L}/u.test(word));
  if (!words.length) return "";
  const first = [...words[0]][0];
  const last = words.length > 1 ? [...words[words.length - 1]][0] : "";
  return `${first}${last}`.toUpperCase();
};
const avatarClass = (id) => `av-${Math.abs(Number(id) || 0) % 4}`;

// Same wording as the chat list preview the server writes.
const previewOf = (message) => {
  const body = String(message?.body || "").replace(/\s+/g, " ").trim();
  switch (message?.type) {
    case "text":
    case "interactive":
    case "button":
      return body || "Message";
    case "image": return body ? `Photo: ${body}` : "Photo";
    case "video": return body ? `Video: ${body}` : "Video";
    case "audio": return message.extra?.voice ? "Voice note" : "Audio";
    case "document": return `Document${message.media?.fileName ? ` · ${message.media.fileName}` : ""}`;
    case "sticker": return "Sticker";
    case "location": return body ? `Location: ${body}` : "Location";
    case "contacts": return body ? `Contact: ${body}` : "Contact";
    default: return "Message";
  }
};

// Messages in the order the server keeps them: by time sent, then by id.
const byTime = (a, b) => {
  const at = toDate(a.sentAt)?.getTime() ?? 0;
  const bt = toDate(b.sentAt)?.getTime() ?? 0;
  return at === bt ? a.id - b.id : at - bt;
};
const mergeMessages = (current, incoming) => {
  const byId = new Map(current.map((message) => [message.id, message]));
  incoming.forEach((message) => byId.set(message.id, message));
  return [...byId.values()].sort(byTime);
};
const earlier = (a, b) => {
  const first = toDate(a);
  const second = toDate(b);
  if (!first) return b;
  if (!second) return a;
  return first <= second ? a : b;
};

const chatTag = (row) => {
  if (row.lead) {
    if (row.lead.booked) return { kind: "booked", text: ["Booked", row.lead.leadCode].filter(Boolean).join(" · ") };
    return { kind: "lead", text: [row.lead.leadCode, row.lead.interestStatus, row.lead.ownerName].filter(Boolean).join(" · ") };
  }
  if (row.nonCustomerCategory) return { kind: "other", text: `Not a customer · ${categoryLabel(row.nonCustomerCategory)}` };
  return { kind: "none", text: "Not in Lead Tracker" };
};

// ---- Icons (inline, so the page needs no icon font) ----

const Svg = ({ size = 15, children, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...rest}>{children}</svg>
);
const SearchIcon = () => <Svg><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></Svg>;
const PersonIcon = () => <Svg size={18}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></Svg>;
const OpenIcon = () => <Svg size={13}><path d="M14 4h6v6" /><path d="M20 4 10 14" /><path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" /></Svg>;
const BackIcon = () => <Svg size={16}><path d="m15 18-6-6 6-6" /></Svg>;
const FileIcon = () => <Svg size={20}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></Svg>;

function Avatar({ id, name, size = 40 }) {
  const initials = initialsOf(name);
  return (
    <span className={`io-chats-avatar ${avatarClass(id)}`} style={size === 40 ? undefined : { width: size, height: size }}>
      {initials || <PersonIcon />}
    </span>
  );
}

// ---- Page ----

export default function ChatsPage({ scope }) {
  const admin = scope === "ADMIN";
  const [theme, setTheme] = useStaffTheme();
  const token = useMemo(() => readToken(scope), [scope]);
  const [auth, setAuth] = useState(token ? "checking" : "login");
  const [me, setMe] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [list, setList] = useState({ chats: [], counts: null, hasMore: false, historyFrom: null });
  const [listLoading, setListLoading] = useState(false);
  const [moreLoading, setMoreLoading] = useState(false);
  const [listLoaded, setListLoaded] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [live, setLive] = useState("connecting");
  const [now, setNow] = useState(() => new Date());

  const listRequest = useRef(0);
  const rowsRef = useRef([]);
  rowsRef.current = list.chats;
  const openIdRef = useRef(null);
  openIdRef.current = openId;
  const refreshOpenChat = useRef(null);

  const signedOut = useCallback((error) => {
    if (!isSignedOut(error)) return false;
    setAuth("login");
    return true;
  }, []);

  useEffect(() => {
    if (!token) {
      setAuth("login");
      return undefined;
    }
    if (admin) {
      setMe({ name: "Admin", team: "Read only" });
      setAuth("ok");
      return undefined;
    }
    let cancelled = false;
    validateLeadTeam(scope, token)
      .then((data) => {
        if (cancelled) return;
        setMe({ name: data?.name || data?.username || "Me", team: scope === "CALL" ? "Call team" : "Chat team" });
        setAuth("ok");
      })
      .catch(() => {
        if (!cancelled) setAuth("login");
      });
    return () => {
      cancelled = true;
    };
  }, [admin, scope, token]);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(queryInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [queryInput]);

  useEffect(() => {
    const ticker = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(ticker);
  }, []);

  // A quiet refresh reloads as many rows as are on screen (in whole pages, so "Load more" carries on
  // from the right place); "more" adds the next page.
  const loadList = useCallback(async ({ silent = false, more = false } = {}) => {
    if (auth !== "ok") return;
    const request = ++listRequest.current;
    const loaded = rowsRef.current.length;
    const params = more
      ? { filter, q: query, page: Math.ceil(loaded / PAGE_SIZE), size: PAGE_SIZE }
      : { filter, q: query, page: 0, size: silent ? Math.min(MAX_PAGE, Math.max(1, Math.ceil(loaded / PAGE_SIZE)) * PAGE_SIZE) : PAGE_SIZE };
    if (more) setMoreLoading(true);
    else if (!silent) setListLoading(true);
    try {
      const data = await listChats(scope, token, params);
      if (request !== listRequest.current) return;
      const rows = data?.chats || [];
      setList((current) => {
        if (!more) return { ...data, chats: rows };
        const known = new Set(current.chats.map((row) => row.id));
        return { ...data, chats: [...current.chats, ...rows.filter((row) => !known.has(row.id))] };
      });
      setListLoaded(true);
    } catch (error) {
      if (request !== listRequest.current) return;
      if (!signedOut(error) && !silent) toast.error(errorText(error, "Could not load the chats"));
    } finally {
      if (request === listRequest.current) {
        setListLoading(false);
        setMoreLoading(false);
      }
    }
  }, [auth, filter, query, scope, token, signedOut]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // Live updates come in bursts (a message, then its delivery ticks, then its photo), so they are
  // gathered for a moment and applied together.
  const pendingIds = useRef(new Set());
  const flushTimer = useRef(null);
  const chatUpdated = useCallback((chatId) => {
    pendingIds.current.add(chatId);
    if (flushTimer.current) return;
    flushTimer.current = window.setTimeout(() => {
      flushTimer.current = null;
      const ids = pendingIds.current;
      pendingIds.current = new Set();
      void loadList({ silent: true });
      if (openIdRef.current != null && ids.has(openIdRef.current)) refreshOpenChat.current?.();
    }, 400);
  }, [loadList]);
  const chatUpdatedRef = useRef(chatUpdated);
  chatUpdatedRef.current = chatUpdated;
  useEffect(() => () => window.clearTimeout(flushTimer.current), []);

  useEffect(() => {
    if (auth !== "ok") return undefined;
    const controller = new AbortController();
    let retryTimer = null;
    let delay = 3_000;
    let connectedBefore = false;
    const connect = async () => {
      setLive("connecting");
      try {
        await connectChatEvents(scope, token, controller.signal, (chatId) => chatUpdatedRef.current(chatId), () => {
          setLive("live");
          delay = 3_000;
          // Back after a drop: catch up on whatever changed meanwhile.
          if (connectedBefore) chatUpdatedRef.current(openIdRef.current ?? -1);
          connectedBefore = true;
        });
      } catch {
        // Dropped or refused; tried again below.
      }
      if (controller.signal.aborted) return;
      setLive("offline");
      retryTimer = window.setTimeout(connect, delay);
      delay = Math.min(delay * 2, 30_000);
    };
    void connect();
    return () => {
      controller.abort();
      window.clearTimeout(retryTimer);
    };
  }, [auth, scope, token]);

  // While live updates are down, the page checks in every 30 seconds instead.
  useEffect(() => {
    if (auth !== "ok" || live === "live") return undefined;
    const poller = window.setInterval(() => chatUpdatedRef.current(openIdRef.current ?? -1), 30_000);
    return () => window.clearInterval(poller);
  }, [auth, live]);

  const chatRead = useCallback((chatId) => {
    setList((current) => {
      const row = current.chats.find((chat) => chat.id === chatId);
      if (!row || !row.unreadCount) return current;
      const counts = current.counts ? { ...current.counts, unread: Math.max(0, (current.counts.unread || 0) - 1) } : current.counts;
      return { ...current, counts, chats: current.chats.map((chat) => (chat.id === chatId ? { ...chat, unreadCount: 0 } : chat)) };
    });
  }, []);

  const chatChanged = useCallback(() => {
    void loadList({ silent: true });
  }, [loadList]);

  const openRow = list.chats.find((row) => row.id === openId) || null;
  const businessNumber = list.chats.find((row) => row.businessNumber)?.businessNumber || "";
  const filters = FILTERS.filter((item) => !(admin && item.teamOnly));
  const searching = Boolean(query) || filter !== "ALL";

  const header = (
    <header className="io-chats-header">
      <div className="io-chats-header-left">
        <span className="io-chats-title">Chats</span>
        {businessNumber && <span className="io-chats-number io-chats-hide-sm">WhatsApp {businessNumber}</span>}
        {auth === "ok" && (
          <span className={`io-chats-live ${live === "live" ? "is-live" : live === "offline" ? "is-offline" : ""}`}>
            <span className="io-chats-live-dot" />
            {live === "live" ? "Live" : live === "offline" ? "Reconnecting…" : "Connecting…"}
          </span>
        )}
      </div>
      <div className="io-chats-header-right">
        <button type="button" className="io-btn" onClick={() => window.open(PORTAL_PATHS[scope], "_blank", "noopener,noreferrer")}>
          <span className="io-chats-hide-sm">Open Lead Tracker</span>
          <span className="d-md-none">Lead Tracker</span>
          <OpenIcon />
        </button>
        {me && (
          <span className="io-chats-me">
            <span className="io-chats-me-initial">{(initialsOf(me.name) || "?").slice(0, 1)}</span>
            <span className="io-chats-me-text io-chats-hide-sm">
              <span className="io-chats-me-name">{me.name}</span>
              <span className="io-chats-me-team">{me.team}</span>
            </span>
          </span>
        )}
        {/* Phones get the theme from the Lead Tracker's switch (same setting), to keep the header on one line. */}
        <span className="io-chats-hide-sm"><StaffThemeSwitch theme={theme} onChange={setTheme} size="sm" /></span>
      </div>
    </header>
  );

  if (auth !== "ok") {
    return (
      <div className="io-chats">
        {header}
        <div className="io-chats-signin">
          {auth === "checking" ? (
            <Spinner />
          ) : (
            <div className="io-chats-signin-card">
              {admin
                ? "Please sign in to the admin panel in another tab, then reload this page."
                : `Please log in to the ${scope === "CALL" ? "Call" : "Chat"} Team portal in another tab first, then reload this page.`}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`io-chats${openId != null ? " has-open" : ""}`}>
      {header}
      <div className="io-chats-body">
        <section className="io-chats-list" aria-label="Chat list">
          <div className="io-chats-search">
            <label>
              <SearchIcon />
              <input type="search" aria-label="Search chats" placeholder="Search name, number or lead ID" value={queryInput} onChange={(event) => setQueryInput(event.target.value)} />
            </label>
          </div>
          <div className="io-chats-filters" role="group" aria-label="Show">
            {filters.map((item) => (
              <button key={item.key} type="button" aria-pressed={filter === item.key} className={`io-chats-filter${filter === item.key ? " is-on" : ""}`} onClick={() => setFilter(item.key)}>
                {item.label}{" "}
                {list.counts && <span className="io-chats-filter-count">{list.counts[item.count] ?? 0}</span>}
              </button>
            ))}
          </div>
          <div className="io-chats-rows">
            {listLoading && !list.chats.length && <div className="io-chats-empty"><Spinner size="sm" className="me-2" />Loading chats…</div>}
            {!listLoading && listLoaded && !list.chats.length && (
              <div className="io-chats-empty">
                {searching ? "No chats match." : "No chats yet. Messages sent to the WhatsApp number show up here as they arrive."}
              </div>
            )}
            {list.chats.map((row) => (
              <ChatRow key={row.id} row={row} open={row.id === openId} now={now} onOpen={() => setOpenId(row.id)} />
            ))}
            {list.hasMore && (
              <div className="io-chats-more">
                <button type="button" className="io-btn io-btn-sm" onClick={() => loadList({ more: true })} disabled={moreLoading}>
                  {moreLoading ? <><Spinner size="sm" />Loading</> : "Load more chats"}
                </button>
              </div>
            )}
          </div>
          {list.historyFrom && <div className="io-chats-list-note">Messages saved here from {fullDate(list.historyFrom)}</div>}
        </section>
        <section className="io-chats-conv" aria-label="Conversation">
          {openId != null ? (
            <Conversation
              key={openId}
              scope={scope}
              token={token}
              admin={admin}
              chatId={openId}
              row={openRow}
              now={now}
              refreshRef={refreshOpenChat}
              onBack={() => setOpenId(null)}
              onRead={chatRead}
              onChanged={chatChanged}
              onSignedOut={signedOut}
            />
          ) : (
            <div className="io-chats-placeholder">Pick a chat on the left to read it.</div>
          )}
        </section>
      </div>
    </div>
  );
}

function ChatRow({ row, open, now, onOpen }) {
  const name = row.contactName || row.customerNumber;
  const tag = chatTag(row);
  const preview = `${row.lastMessageDirection === "OUT" ? "You: " : ""}${row.lastMessagePreview || ""}`;
  return (
    <button type="button" className={`io-chats-row${open ? " is-open" : ""}${row.unreadCount ? " has-unread" : ""}`} onClick={onOpen} aria-current={open ? "true" : undefined}>
      <Avatar id={row.id} name={row.contactName} />
      <span className="io-chats-row-main">
        <span className="io-chats-row-line">
          <span className="io-chats-row-name" title={row.contactName ? `${row.contactName} · ${row.customerNumber}` : row.customerNumber}>{name}</span>
          <span className="io-chats-row-time">{listTime(row.lastMessageAt, now)}</span>
        </span>
        <span className="io-chats-row-line">
          <span className="io-chats-row-preview">{preview}</span>
          {row.unreadCount > 0 && <span className="io-chats-unread" aria-label={`${row.unreadCount} unread`}>{row.unreadCount > 99 ? "99+" : row.unreadCount}</span>}
        </span>
        <span className={`io-chats-tag tag-${tag.kind}`}>{tag.text}</span>
      </span>
    </button>
  );
}

// ---- One chat ----

function Conversation({ scope, token, admin, chatId, row, now, refreshRef, onBack, onRead, onChanged, onSignedOut }) {
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasOlder, setHasOlder] = useState(false);
  const [validUntil, setValidUntil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [olderLoading, setOlderLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [marking, setMarking] = useState(false);
  const [undoing, setUndoing] = useState(false);

  const scrollerRef = useRef(null);
  const scrollIntent = useRef("bottom");
  const nearBottom = useRef(true);
  const lastMarked = useRef(0);
  const messagesRef = useRef([]);
  messagesRef.current = messages;

  const loadDetail = useCallback(async () => {
    try {
      setDetail(await getChat(scope, token, chatId));
    } catch (error) {
      if (!onSignedOut(error)) setLoadError(errorText(error, "Could not load this chat"));
    }
  }, [scope, token, chatId, onSignedOut]);

  const loadLatest = useCallback(async ({ initial = false } = {}) => {
    try {
      const page = await getChatMessages(scope, token, chatId, { limit: PAGE_SIZE });
      const fresh = page?.messages || [];
      const current = messagesRef.current;
      if (initial) {
        scrollIntent.current = "bottom";
        setMessages(fresh);
        setHasOlder(Boolean(page?.hasOlder));
        setValidUntil(page?.mediaLinksValidUntil || null);
        return;
      }
      scrollIntent.current = nearBottom.current ? "bottom" : "none";
      const newestKnown = current[current.length - 1];
      // More arrived than one page holds: start again from the newest page.
      const gap = newestKnown && page?.hasOlder && fresh.length && !fresh.some((message) => message.id === newestKnown.id)
        && byTime(fresh[0], newestKnown) > 0;
      if (gap) {
        setMessages(fresh);
        setHasOlder(true);
        setValidUntil(page?.mediaLinksValidUntil || null);
      } else {
        setMessages((existing) => mergeMessages(existing, fresh));
        setValidUntil((previous) => (current.length <= PAGE_SIZE ? page?.mediaLinksValidUntil || null : earlier(previous, page?.mediaLinksValidUntil)));
      }
    } catch (error) {
      if (!onSignedOut(error) && initial) setLoadError(errorText(error, "Could not load the messages"));
    } finally {
      if (initial) setLoading(false);
    }
  }, [scope, token, chatId, onSignedOut]);

  useEffect(() => {
    setLoading(true);
    void loadDetail();
    void loadLatest({ initial: true });
  }, [loadDetail, loadLatest]);

  // The page calls this when a live update is for this chat.
  useEffect(() => {
    const refresh = () => {
      void loadDetail();
      void loadLatest();
    };
    refreshRef.current = refresh;
    return () => {
      if (refreshRef.current === refresh) refreshRef.current = null;
    };
  }, [refreshRef, loadDetail, loadLatest]);

  const loadOlder = async () => {
    const first = messagesRef.current[0];
    if (!first) return;
    setOlderLoading(true);
    try {
      const page = await getChatMessages(scope, token, chatId, { beforeId: first.id, limit: PAGE_SIZE });
      const element = scrollerRef.current;
      scrollIntent.current = element ? { height: element.scrollHeight, top: element.scrollTop } : "none";
      setMessages((existing) => mergeMessages(existing, page?.messages || []));
      setHasOlder(Boolean(page?.hasOlder));
      setValidUntil((previous) => earlier(previous, page?.mediaLinksValidUntil));
    } catch (error) {
      if (!onSignedOut(error)) toast.error(errorText(error, "Could not load older messages"));
    } finally {
      setOlderLoading(false);
    }
  };

  // Photo, video and file links last an hour; fetch fresh ones shortly before they stop working.
  useEffect(() => {
    const until = toDate(validUntil);
    if (!until) return undefined;
    const timer = window.setTimeout(async () => {
      const count = messagesRef.current.length;
      try {
        const page = await getChatMessages(scope, token, chatId, { limit: Math.min(MAX_PAGE, Math.max(PAGE_SIZE, count)) });
        scrollIntent.current = "none";
        if (count > MAX_PAGE) {
          setMessages(page?.messages || []);
          setHasOlder(Boolean(page?.hasOlder));
        } else {
          setMessages((existing) => mergeMessages(existing, page?.messages || []));
        }
        setValidUntil(page?.mediaLinksValidUntil || null);
      } catch {
        // Tried again with the next live update.
      }
    }, Math.max(60_000, until.getTime() - Date.now()));
    return () => window.clearTimeout(timer);
  }, [validUntil, scope, token, chatId]);

  useLayoutEffect(() => {
    const element = scrollerRef.current;
    if (!element) return;
    const intent = scrollIntent.current;
    scrollIntent.current = "none";
    if (intent === "bottom") {
      element.scrollTop = element.scrollHeight;
      nearBottom.current = true;
    } else if (intent && typeof intent === "object") {
      element.scrollTop = element.scrollHeight - intent.height + intent.top;
    }
  }, [messages, loading]);

  const onScroll = () => {
    const element = scrollerRef.current;
    if (element) nearBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < 160;
  };
  // A photo that finishes loading makes the chat taller; stay at the bottom if that's where we were.
  const onMediaLoad = useCallback(() => {
    const element = scrollerRef.current;
    if (element && nearBottom.current) element.scrollTop = element.scrollHeight;
  }, []);

  // Read marks are per person and only move forward; nothing is marked while the tab is hidden.
  const markRead = useCallback(() => {
    if (document.visibilityState !== "visible") return;
    const lastId = messagesRef.current.reduce((max, message) => Math.max(max, message.id), 0);
    if (!lastId || lastId <= lastMarked.current) return;
    lastMarked.current = lastId;
    markChatRead(scope, token, chatId, lastId)
      .then(() => onRead(chatId))
      .catch(() => {
        lastMarked.current = 0;
      });
  }, [scope, token, chatId, onRead]);
  useEffect(() => {
    markRead();
  }, [messages, markRead]);
  useEffect(() => {
    document.addEventListener("visibilitychange", markRead);
    return () => document.removeEventListener("visibilitychange", markRead);
  }, [markRead]);

  // Reactions sit on the message they react to; one whose message isn't loaded gets a line of its own.
  const { visible, reactions, byWamid } = useMemo(() => {
    const targets = new Map();
    const loadedWamids = new Map(messages.filter((message) => message.type !== "reaction").map((message) => [message.wamid, message]));
    const lastReaction = new Map();
    messages.forEach((message) => {
      if (message.type !== "reaction" || !message.contextWamid) return;
      const entry = targets.get(message.contextWamid) || new Map();
      if (message.body) entry.set(message.direction, message.body);
      else entry.delete(message.direction);
      targets.set(message.contextWamid, entry);
      lastReaction.set(`${message.contextWamid}|${message.direction}`, message.id);
    });
    const shown = messages.filter((message) => {
      if (message.type !== "reaction") return true;
      if (!message.contextWamid || loadedWamids.has(message.contextWamid) || !message.body) return false;
      return lastReaction.get(`${message.contextWamid}|${message.direction}`) === message.id;
    });
    return { visible: shown, reactions: targets, byWamid: loadedWamids };
  }, [messages]);

  const portal = PORTAL_PATHS[scope];
  const name = detail?.contactName || row?.contactName || detail?.customerNumber || row?.customerNumber || "Chat";
  const number = detail?.customerNumber || row?.customerNumber || "";

  const openLead = () => {
    if (!detail?.lead) return;
    openInLeadTracker(portal, "openLead", { leadId: detail.lead.id, leadCode: detail.lead.leadCode });
  };
  const createLead = () => {
    if (!detail) return;
    openInLeadTracker(portal, "newLead", { customerNumber: detail.customerNumberDigits });
    toast.info("The Lead Tracker opened in a new tab. Once the lead is saved, this chat links to it by itself.");
  };
  const changed = () => {
    void loadDetail();
    onChanged();
  };
  const undoNotCustomer = async () => {
    setUndoing(true);
    try {
      await unmarkChatNotCustomer(scope, token, chatId);
      toast.success(`${number} is back in Not in Lead Tracker`);
      changed();
    } catch (error) {
      if (!onSignedOut(error)) toast.error(errorText(error, "Could not undo"));
    } finally {
      setUndoing(false);
    }
  };

  const nodes = [];
  if (!hasOlder && visible.length) nodes.push(<div key="start" className="io-chats-system">Start of this chat as saved here</div>);
  let lastDay = null;
  visible.forEach((message) => {
    const sent = toDate(message.sentAt);
    const day = sent ? startOfDay(sent).getTime() : null;
    if (sent && day !== lastDay) {
      nodes.push(<div key={`day-${day}`} className="io-chats-day">{dayLabel(sent, now)}</div>);
      lastDay = day;
    }
    if (message.type === "reaction") {
      nodes.push(
        <div key={message.id} className="io-chats-system">
          {message.direction === "IN" ? "Customer" : "We"} reacted {message.body} to an earlier message{sent ? ` · ${clock(sent)}` : ""}
        </div>
      );
      return;
    }
    nodes.push(
      <MessageBubble
        key={message.id}
        message={message}
        reactions={reactions.get(message.wamid)}
        quoted={message.contextWamid ? byWamid.get(message.contextWamid) : null}
        onMediaLoad={onMediaLoad}
      />
    );
  });

  return (
    <>
      <div className="io-chats-conv-head">
        <button type="button" className="io-btn io-btn-icon io-chats-back" onClick={onBack} aria-label="Back to the chat list"><BackIcon /></button>
        <Avatar id={chatId} name={detail?.contactName || row?.contactName} size={38} />
        <div style={{ minWidth: 0 }}>
          <div className="io-chats-conv-name">{name}</div>
          <div className="io-chats-conv-sub">{[number, detail?.businessNumber ? `on ${detail.businessNumber}` : ""].filter(Boolean).join(" · ")}</div>
        </div>
      </div>
      <LeadStrip
        detail={detail}
        admin={admin}
        now={now}
        busy={undoing}
        onOpenLead={openLead}
        onAddToLead={() => setLinking(true)}
        onCreateLead={createLead}
        onNotCustomer={() => setMarking(true)}
        onUndoNotCustomer={undoNotCustomer}
      />
      <div className="io-chats-messages" ref={scrollerRef} onScroll={onScroll}>
        {hasOlder && (
          <div className="text-center">
            <button type="button" className="io-btn io-btn-sm" onClick={loadOlder} disabled={olderLoading}>
              {olderLoading ? <><Spinner size="sm" />Loading</> : "Load older messages"}
            </button>
          </div>
        )}
        {loading && <div className="io-chats-system"><Spinner size="sm" className="me-2" />Loading messages…</div>}
        {!loading && loadError && <div className="io-chats-system">{loadError}</div>}
        {!loading && !loadError && !visible.length && <div className="io-chats-system">No messages saved for this chat yet.</div>}
        {nodes}
      </div>
      <ReplyNotice detail={detail} now={now} />
      {!admin && (
        <>
          <LinkChatModal show={linking} scope={scope} token={token} chatId={chatId} number={number} onHide={() => setLinking(false)} onLinked={() => { setLinking(false); changed(); }} />
          <NotCustomerChatModal show={marking} scope={scope} token={token} chatId={chatId} number={number} onHide={() => setMarking(false)} onSaved={() => { setMarking(false); changed(); }} />
        </>
      )}
    </>
  );
}

function LeadStrip({ detail, admin, now, busy, onOpenLead, onAddToLead, onCreateLead, onNotCustomer, onUndoNotCustomer }) {
  if (!detail) return <div className="io-chats-strip" />;
  const lead = detail.lead;
  if (lead) {
    const people = [
      lead.numberOfPersons ? `${lead.numberOfPersons} adult${lead.numberOfPersons === 1 ? "" : "s"}` : "",
      lead.numberOfKids ? `${lead.numberOfKids} kid${lead.numberOfKids === 1 ? "" : "s"}` : "",
    ].filter(Boolean).join(", ");
    const trip = [lead.destinationName, lead.plannedDates, people].filter(Boolean).join(" · ");
    const teamName = { CALL: "Call team", CHAT: "Chat team", ADMIN: "Admin team" }[lead.team] || "";
    const owner = [lead.ownerName, teamName].filter(Boolean).join(" · ");
    return (
      <div className="io-chats-strip">
        <span className="io-chats-strip-code">{lead.leadCode}</span>
        {lead.interestStatus && <span className="io-chats-strip-status">{lead.interestStatus}</span>}
        {lead.bookingId && <span className="io-chats-strip-booked">Booked</span>}
        {owner && <span>{owner}</span>}
        {trip && <><span className="io-chats-strip-sep">|</span><span>{trip}</span></>}
        {lead.nextFollowUpDateTime && <><span className="io-chats-strip-sep">|</span><span>Next follow-up <b>{whenText(lead.nextFollowUpDateTime, now)}</b></span></>}
        <span className="io-chats-strip-actions">
          <button type="button" className="io-btn io-btn-sm io-btn-lead" onClick={onOpenLead}>Open lead</button>
        </span>
      </div>
    );
  }
  if (detail.nonCustomerCategory) {
    return (
      <div className="io-chats-strip is-other">
        <span className="io-chats-strip-code">Not a customer · {categoryLabel(detail.nonCustomerCategory)}</span>
        {detail.nonCustomerNote && <span>{detail.nonCustomerNote}</span>}
        {detail.nonCustomerMarkedBy && <span>Marked by {detail.nonCustomerMarkedBy}</span>}
        {!admin && (
          <span className="io-chats-strip-actions">
            <button type="button" className="io-btn io-btn-sm" onClick={onUndoNotCustomer} disabled={busy}>{busy ? <Spinner size="sm" /> : "Undo"}</button>
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="io-chats-strip is-amber">
      <span className="io-chats-strip-code">Not in Lead Tracker</span>
      <span>{admin ? "The Call and Chat Teams can add this number to a lead." : "No lead has this number yet."}</span>
      {!admin && (
        <span className="io-chats-strip-actions">
          <button type="button" className="io-btn io-btn-sm io-btn-amber" onClick={onAddToLead}>Add to a lead</button>
          <button type="button" className="io-btn io-btn-sm io-btn-primary" onClick={onCreateLead}>Create lead</button>
          <button type="button" className="io-btn io-btn-sm io-btn-amber" onClick={onNotCustomer}>Not a customer</button>
        </span>
      )}
    </div>
  );
}

function ReplyNotice({ detail, now }) {
  const ends = toDate(detail?.replyWindowEndsAt);
  let windowLine = null;
  if (ends && ends > now) windowLine = <span className="io-chats-window-open">Free reply window open · {timeLeft(ends - now)} left</span>;
  else if (ends) windowLine = <span className="io-chats-window-closed">Free reply window closed · {whenText(ends, now)}</span>;
  return (
    <div className="io-chats-footer">
      <div className="io-chats-footer-title">Replying from this page comes in the next step</div>
      <div className="io-chats-footer-text">For now, reply from the WhatsApp Business app or WhatsApp Web. Those replies show here too.</div>
      {windowLine && <div className="io-chats-footer-text mt-1">{windowLine}</div>}
    </div>
  );
}

// ---- Messages ----

function Ticks({ status }) {
  const value = String(status || "").toLowerCase();
  if (value === "failed") return <span className="io-ticks is-failed">Not delivered</span>;
  if (value === "read") return <span className="io-ticks is-read" title="Read">✓✓</span>;
  if (value === "delivered") return <span className="io-ticks" title="Delivered">✓✓</span>;
  if (value === "sent") return <span className="io-ticks" title="Sent">✓</span>;
  return null;
}

function MessageBubble({ message, reactions, quoted, onMediaLoad }) {
  const ours = message.direction === "OUT";
  const kind = !ours ? "is-in" : message.source === "BUSINESS_APP" ? "is-phone" : "is-ours";
  const sent = toDate(message.sentAt);
  const referral = message.extra?.referral;
  const reactionList = reactions ? [...reactions.entries()] : [];
  return (
    <div className={`io-bubble ${kind}`}>
      {ours && message.senderLabel && <div className="io-bubble-by">{message.senderLabel}</div>}
      {referral && <div className="io-bubble-referral">Came from an ad{referral.headline ? `: ${referral.headline}` : ""}</div>}
      {message.contextWamid && <div className="io-bubble-quote">{quoted ? previewOf(quoted) : "Reply to an earlier message"}</div>}
      <MessageContent message={message} onMediaLoad={onMediaLoad} />
      <div className="io-bubble-meta">
        {sent ? clock(sent) : ""}
        {ours && <Ticks status={message.deliveryStatus} />}
      </div>
      {reactionList.length > 0 && (
        <div className="io-bubble-reactions">
          {reactionList.map(([direction, emoji]) => <span key={direction} title={direction === "IN" ? "Customer's reaction" : "Our reaction"}>{emoji}</span>)}
        </div>
      )}
    </div>
  );
}

function MediaWait({ noun, media }) {
  const status = media?.status;
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);
  const saving = status === "PENDING" || status === "DOWNLOADING";
  let text = Noun;
  if (saving) text = `Saving the ${noun}…`;
  else if (status === "EXPIRED") text = `${Noun} is no longer available from WhatsApp`;
  else if (status === "FAILED") text = `${Noun} could not be saved`;
  return <div className="io-media-wait">{saving && <Spinner size="sm" />}{text}</div>;
}

function MessageContent({ message, onMediaLoad }) {
  const { type, body, media, extra } = message;
  const caption = body ? <div className="io-bubble-text">{body}</div> : null;
  switch (type) {
    case "text":
    case "interactive":
    case "button":
      return <div className="io-bubble-text">{body || ""}</div>;
    case "image":
      return (
        <>
          {media?.url ? (
            <a href={media.url} target="_blank" rel="noreferrer">
              <img className="io-media-photo" src={media.url} alt={body || "Photo from the chat"} onLoad={onMediaLoad} />
            </a>
          ) : <MediaWait noun="photo" media={media} />}
          {caption}
        </>
      );
    case "sticker":
      return media?.url ? <img className="io-media-sticker" src={media.url} alt="Sticker" onLoad={onMediaLoad} /> : <MediaWait noun="sticker" media={media} />;
    case "video":
      return (
        <>
          {media?.url ? <video className="io-media-video" src={media.url} controls preload="metadata" onLoadedMetadata={onMediaLoad} /> : <MediaWait noun="video" media={media} />}
          {caption}
        </>
      );
    case "audio": {
      const noun = extra?.voice ? "voice note" : "audio";
      if (!media?.url) return <MediaWait noun={noun} media={media} />;
      return (
        <div>
          <div className="io-bubble-by">{extra?.voice ? "Voice note" : "Audio"}</div>
          <audio className="io-media-audio" src={media.url} controls preload="metadata" />
        </div>
      );
    }
    case "document": {
      const name = media?.fileName || "Document";
      const size = fileSize(media?.size);
      return (
        <>
          {media?.url ? (
            <a className="io-media-doc" href={media.url} target="_blank" rel="noreferrer">
              <FileIcon />
              <span style={{ minWidth: 0 }}>
                <span className="io-media-doc-name d-block">{name}</span>
                <span className="io-media-doc-size">{[size, "Download"].filter(Boolean).join(" · ")}</span>
              </span>
            </a>
          ) : (
            <>
              <MediaWait noun="document" media={media} />
              <div className="io-media-doc-size">{name}</div>
            </>
          )}
          {caption}
        </>
      );
    }
    case "location": {
      const place = extra?.location || {};
      const hasPoint = place.latitude != null && place.longitude != null;
      return (
        <div>
          <div className="fw-semibold">{place.name || "Location"}</div>
          {place.address && <div className="io-bubble-text">{place.address}</div>}
          {hasPoint && (
            <a className="io-link" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${place.latitude},${place.longitude}`)}`} target="_blank" rel="noreferrer">
              Open in Google Maps
            </a>
          )}
        </div>
      );
    }
    case "contacts": {
      const contacts = Array.isArray(extra?.contacts) ? extra.contacts : [];
      if (!contacts.length) return <div className="io-bubble-text">{body || "Contact"}</div>;
      return (
        <div>
          {contacts.map((contact, index) => (
            <div key={index} className={index ? "mt-1" : undefined}>
              <div className="fw-semibold">{contact?.name?.formatted_name || "Contact"}</div>
              {(contact?.phones || []).map((phone, phoneIndex) => <div key={phoneIndex}>{phone?.phone || phone?.wa_id}</div>)}
            </div>
          ))}
        </div>
      );
    }
    default:
      return <div className="io-bubble-note">This kind of message can't be shown here yet - open WhatsApp to see it.{body ? ` (${body})` : ""}</div>;
  }
}

// ---- Lead actions (Call and Chat Teams only) ----

function LinkChatModal({ show, scope, token, chatId, number, onHide, onLinked }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (!show) return;
    setQuery("");
    setOptions([]);
  }, [show]);

  useEffect(() => {
    const text = query.trim();
    if (!show || text.length < 3) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const rows = await searchChatLeadOptions(scope, token, text);
        if (!cancelled) setOptions(rows || []);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, show, scope, token]);

  const link = async (option, replaceAlternate = false) => {
    setSavingId(option.id);
    try {
      const result = await linkChatToLead(scope, token, chatId, option.id, replaceAlternate);
      toast.success(`${number} added to ${result?.leadCode || option.leadId}`);
      onLinked();
    } catch (error) {
      const message = errorText(error, "");
      // The lead already has a different alternate number: only replaced when the employee says so.
      if (error?.response?.status === 409 && !replaceAlternate) {
        if (window.confirm(message || "This lead already has an alternate number. Replace it?")) await link(option, true);
      } else {
        toast.error(message || "Could not add the number to the lead");
      }
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Modal show={show} onHide={() => { if (!savingId) onHide(); }} centered>
      <Modal.Header closeButton={!savingId}><Modal.Title>Add {number} to a lead</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-2">It becomes the lead's alternate number, so this chat and the number's calls show on the lead.</div>
        <Form.Control autoFocus placeholder="Lead ID or customer number" value={query} onChange={(event) => setQuery(event.target.value)} disabled={Boolean(savingId)} />
        {searching && <div className="small text-muted mt-2"><Spinner size="sm" className="me-2" />Searching...</div>}
        {!searching && query.trim().length >= 3 && !options.length && <div className="small text-muted mt-2">No matching lead.</div>}
        {options.length > 0 && (
          <ListGroup className="mt-2">
            {options.map((option) => (
              <ListGroup.Item key={option.id} className="d-flex justify-content-between align-items-center gap-2">
                <div>
                  <div className="fw-semibold">{option.leadId} <span className="fw-normal">{option.customerNumber}</span></div>
                  <div className="small text-muted">{[option.destinationName, option.interestStatus, option.assignedTeam].filter(Boolean).join(" - ")}</div>
                  {option.alternateNumber && <div className="small text-muted">Alternate: {option.alternateNumber}</div>}
                </div>
                <Button size="sm" onClick={() => link(option)} disabled={Boolean(savingId)}>{savingId === option.id ? <Spinner size="sm" /> : "Add"}</Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Modal.Body>
    </Modal>
  );
}

function NotCustomerChatModal({ show, scope, token, chatId, number, onHide, onSaved }) {
  const [category, setCategory] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setCategory("");
    setNote("");
  }, [show]);

  const save = async (event) => {
    event.preventDefault();
    if (!category) return;
    setSaving(true);
    try {
      await markChatNotCustomer(scope, token, chatId, category, note);
      toast.success(`${number} marked as ${categoryLabel(category).toLowerCase()}`);
      onSaved();
    } catch (error) {
      toast.error(errorText(error, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={() => { if (!saving) onHide(); }} centered>
      <Form onSubmit={save}>
        <Modal.Header closeButton={!saving}><Modal.Title>{number} is not a customer</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="small text-muted mb-2">The chat leaves the No lead list for everyone. The same mark hides the number's calls from Unlinked Calls.</div>
          <Form.Label>Who is it?</Form.Label>
          <div className="d-flex flex-wrap gap-3 mb-3">
            {NON_CUSTOMER_CATEGORIES.map(([value, label]) => (
              <Form.Check key={value} type="radio" id={`chat-non-customer-${value}`} name="chat-non-customer-category" label={label} checked={category === value} onChange={() => setCategory(value)} disabled={saving} />
            ))}
          </div>
          <Form.Label>Note (optional)</Form.Label>
          <Form.Control value={note} maxLength={255} placeholder="e.g. hotel name, driver name" onChange={(event) => setNote(event.target.value)} disabled={saving} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={!category || saving}>{saving ? <Spinner size="sm" /> : "Save"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
